import Observer from './observer.js';
import { isElementNode, hasTag } from '../guards.js';
import IFrameObserver from './iframe_observer.js';
import ShadowRootObserver from './shadow_root_observer.js';
import IFrameOffsets from './iframe_offsets.js';
import { CreateDocument, SetNodeAttribute } from '../messages.gen.js';
import { IN_BROWSER, hasOpenreplayAttribute, canAccessIframe } from '../../utils.js';
export var InlineCssMode;
(function (InlineCssMode) {
    InlineCssMode[InlineCssMode["Unset"] = -1] = "Unset";
    /** default behavior -- will parse and cache the css file on backend */
    InlineCssMode[InlineCssMode["Disabled"] = 0] = "Disabled";
    /** will attempt to record the linked css file as AdoptedStyleSheet object */
    InlineCssMode[InlineCssMode["Inline"] = 1] = "Inline";
    /** will fetch the file, then simulate AdoptedStyleSheets behavior programmaticaly for the replay */
    InlineCssMode[InlineCssMode["InlineFetched"] = 2] = "InlineFetched";
    /** will fetch the file, then save it as plain css inside <style> node */
    InlineCssMode[InlineCssMode["PlainFetched"] = 3] = "PlainFetched";
})(InlineCssMode || (InlineCssMode = {}));
const localhostStylesDoc = 'https://docs.openreplay.com/en/troubleshooting/localhost/';
function getInlineOptions(mode, logger) {
    switch (mode) {
        case InlineCssMode.Inline:
            return {
                inlineRemoteCss: true,
                inlinerOptions: {
                    forceFetch: false,
                    forcePlain: false,
                },
            };
        case InlineCssMode.InlineFetched:
            return {
                inlineRemoteCss: true,
                inlinerOptions: {
                    forceFetch: true,
                    forcePlain: false,
                },
            };
        case InlineCssMode.PlainFetched:
            return {
                inlineRemoteCss: true,
                inlinerOptions: {
                    forceFetch: true,
                    forcePlain: true,
                },
            };
        case InlineCssMode.Unset:
            const isLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/?/.test(window.location.href);
            if (isLocalhost) {
                logger(`Enabling InlineCssMode.PlainFetched by default on localhost to preserve css styles, refer to ${localhostStylesDoc} for details, set InlineCssMode to 0 to skip this behavior`);
                return {
                    inlineRemoteCss: true,
                    inlinerOptions: {
                        forceFetch: true,
                        forcePlain: true,
                    },
                };
            }
            else {
                return {
                    inlineRemoteCss: false,
                    inlinerOptions: {
                        forceFetch: false,
                        forcePlain: false,
                    },
                };
            }
        case InlineCssMode.Disabled:
        default:
            return {
                inlineRemoteCss: false,
                inlinerOptions: {
                    forceFetch: false,
                    forcePlain: false,
                },
            };
    }
}
const attachShadowNativeFn = IN_BROWSER ? Element.prototype.attachShadow : () => new ShadowRoot();
export default class TopObserver extends Observer {
    constructor(params) {
        const opts = Object.assign({
            captureIFrames: true,
            disableSprites: false,
            inlineCss: 0,
        }, params.options);
        const observerOptions = {
            disableSprites: opts.disableSprites,
            disableThrottling: opts.disableThrottling,
            ...getInlineOptions(opts.inlineCss, console.warn),
        };
        super(params.app, true, observerOptions);
        this.iframeOffsets = new IFrameOffsets();
        this.contextCallbacks = [];
        // Attached once per Tracker instance
        this.contextsSet = new WeakSet();
        this.iframeObserversArr = [];
        this.iframeObservers = new WeakMap();
        this.docObservers = new WeakMap();
        this.shadowRootObservers = new WeakMap();
        this.app = params.app;
        this.options = opts;
        // IFrames
        this.app.nodes.attachNodeCallback((node) => {
            if (hasTag(node, 'iframe') &&
                ((this.options.captureIFrames && !hasOpenreplayAttribute(node, 'obscured')) ||
                    hasOpenreplayAttribute(node, 'capture'))) {
                this.handleIframe(node);
            }
        });
        // ShadowDOM
        this.app.nodes.attachNodeCallback((node) => {
            if (isElementNode(node) && node.shadowRoot !== null) {
                this.handleShadowRoot(node.shadowRoot);
            }
        });
    }
    attachContextCallback(cb) {
        this.contextCallbacks.push(cb);
    }
    getDocumentOffset(doc) {
        return this.iframeOffsets.getDocumentOffset(doc);
    }
    handleIframe(iframe) {
        // setTimeout is required. Otherwise some event listeners (scroll, mousemove) applied in modules
        // do not work on the iframe document when it 've been loaded dynamically ((why?))
        const handle = this.app.safe(() => setTimeout(() => {
            const id = this.app.nodes.getID(iframe);
            if (id === undefined || !canAccessIframe(iframe))
                return;
            const currentWin = iframe.contentWindow;
            const currentDoc = iframe.contentDocument;
            if (!currentDoc) {
                this.app.debug.warn('no doc for iframe found', iframe);
                return;
            }
            if (currentDoc && this.docObservers.has(currentDoc)) {
                this.app.debug.info('doc already observed for', id);
                return;
            }
            const observer = new IFrameObserver(this.app, false, {
                disableSprites: this.options.disableSprites,
                ...getInlineOptions(this.options.inlineCss, console.warn),
            });
            this.iframeObservers.set(iframe, observer);
            this.docObservers.set(currentDoc, observer);
            this.iframeObserversArr.push(observer);
            observer.observe(iframe);
            this.iframeOffsets.observe(iframe);
            if (currentWin &&
                // Sometimes currentWin.window is null (not in specification). Such window object is not functional
                currentWin === currentWin.window &&
                !this.contextsSet.has(currentWin) // for each context callbacks called once per Tracker (TopObserver) instance
            //TODO: more explicit logic
            ) {
                this.contextsSet.add(currentWin);
                // @ts-ignore https://github.com/microsoft/TypeScript/issues/41684
                this.contextCallbacks.forEach((cb) => cb(currentWin));
            }
            // we need this delay because few iframes stacked one in another with rapid updates will break the player (or browser engine rather?)
        }, 250));
        iframe.addEventListener('load', handle);
        handle();
    }
    handleShadowRoot(shRoot) {
        const observer = new ShadowRootObserver(this.app, false, {
            disableSprites: this.options.disableSprites,
            ...getInlineOptions(this.options.inlineCss, console.warn),
        });
        this.shadowRootObservers.set(shRoot, observer);
        observer.observe(shRoot.host);
    }
    observe() {
        // Protection from several subsequent calls?
        const observer = this;
        Element.prototype.attachShadow = function () {
            // eslint-disable-next-line
            const shadow = attachShadowNativeFn.apply(this, arguments);
            observer.handleShadowRoot(shadow);
            return shadow;
        };
        this.resetThrottledSetNodeData();
        this.app.nodes.clear();
        // Can observe documentElement (<html>) here, because it is not supposed to be changing.
        // However, it is possible in some exotic cases and may cause an ignorance of the newly created <html>
        // In this case context.document have to be observed, but this will cause
        // the change in the re-player behaviour caused by CreateDocument message:
        //   the 0-node ("fRoot") will become #document rather than documentElement as it is now.
        // Alternatively - observe(#document) then bindNode(documentElement)
        this.observeRoot(window.document, () => {
            this.app.send(CreateDocument());
            // it has no node_id here
            this.app.nodes.callNodeCallbacks(document, true);
        }, window.document.documentElement);
        // DEBUG orload
        const markCandidates = [
            document.head,
            document.body,
            document.body ? document.body.querySelector('div') : null,
        ];
        for (const candidate of markCandidates) {
            const markId = candidate ? this.app.nodes.getID(candidate) : undefined;
            if (markId !== undefined) {
                this.app.send(SetNodeAttribute(markId, 'orloaded', 'true'));
                break;
            }
        }
    }
    crossdomainObserve(rootNodeId, frameOder, frameLevel) {
        const observer = this;
        Element.prototype.attachShadow = function () {
            // eslint-disable-next-line
            const shadow = attachShadowNativeFn.apply(this, arguments);
            observer.handleShadowRoot(shadow);
            return shadow;
        };
        this.app.nodes.clear();
        this.app.nodes.crossdomainMode(frameLevel, frameOder);
        const iframeObserver = new IFrameObserver(this.app, false, {
            disableSprites: this.options.disableSprites,
            ...getInlineOptions(this.options.inlineCss, console.warn),
        });
        this.iframeObservers.set(window.document, iframeObserver);
        iframeObserver.syntheticObserve(rootNodeId, window.document);
    }
    disconnect() {
        this.iframeOffsets.clear();
        Element.prototype.attachShadow = attachShadowNativeFn;
        this.iframeObserversArr.forEach((observer) => observer.disconnect());
        this.iframeObserversArr = [];
        this.iframeObservers = new WeakMap();
        this.shadowRootObservers = new WeakMap();
        this.docObservers = new WeakMap();
        super.disconnect();
    }
}
